import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '../stores/user'
import { ElMessage } from 'element-plus'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// Configure NProgress
NProgress.configure({ showSpinner: false })

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/home/HomePage.vue'),
    meta: { title: '多量IP - 专业IP代理服务平台', public: true },
  },
  {
    path: '/auth',
    component: () => import('../layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('../views/auth/Login.vue'),
        meta: { title: '登录', public: true },
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('../views/auth/Register.vue'),
        meta: { title: '注册', public: true },
      },
    ],
  },
  {
    path: '/',
    component: () => import('../layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/dashboard/index.vue'),
        meta: { title: '控制台' },
      },
      {
        path: 'proxy/extract',
        redirect: '/user/packages',
      },
      {
        path: 'proxy/my',
        name: 'MyProxies',
        component: () => import('../views/proxy/My.vue'),
        meta: { title: '我的代理' },
      },
      {
        path: 'packages',
        name: 'Packages',
        component: () => import('../views/order/Packages.vue'),
        meta: { title: '套餐购买' },
      },
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('../views/order/Orders.vue'),
        meta: { title: '我的订单' },
      },
      // 新增：我的套餐页面
      {
        path: 'user/packages',
        name: 'MyPackages',
        component: () => import('../views/user/MyPackages.vue'),
        meta: { title: '我的套餐' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('../views/user/Profile.vue'),
        meta: { title: '个人中心' },
      },
      // Admin routes
      {
        path: 'admin',
        name: 'Admin',
        component: () => import('../views/admin/Dashboard.vue'),
        meta: { title: '管理后台', requiresAdmin: true },
      },
      {
        path: 'admin/proxy-ips',
        name: 'AdminProxyIPs',
        component: () => import('../views/admin/ProxyIPs.vue'),
        meta: { title: 'IP管理', requiresAdmin: true },
      },
      {
        path: 'admin/packages',
        name: 'AdminPackages',
        component: () => import('../views/admin/Packages.vue'),
        meta: { title: '套餐管理', requiresAdmin: true },
      },
      // 新增：用户套餐分配
      {
        path: 'admin/user-packages',
        name: 'AdminUserPackages',
        component: () => import('../views/admin/UserPackages.vue'),
        meta: { title: '用户套餐分配', requiresAdmin: true },
      },
      // 新增：供应商管理
      {
        path: 'admin/suppliers',
        name: 'AdminSuppliers',
        component: () => import('../views/admin/Suppliers.vue'),
        meta: { title: '供应商管理', requiresAdmin: true },
      },
      {
        path: 'admin/orders',
        name: 'AdminOrders',
        component: () => import('../views/admin/Orders.vue'),
        meta: { title: '订单管理', requiresAdmin: true },
      },
      {
        path: 'admin/users',
        name: 'AdminUsers',
        component: () => import('../views/admin/Users.vue'),
        meta: { title: '用户管理', requiresAdmin: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: { title: '页面不存在', public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// Route guard
router.beforeEach((to, from, next) => {
  NProgress.start()

  const userStore = useUserStore()
  const isLoggedIn = userStore.isLoggedIn
  const isAdmin = userStore.user?.is_admin

  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} - 多量IP`
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !isLoggedIn) {
    ElMessage.warning('请先登录')
    next('/auth/login')
    return
  }

  // Check if route requires admin
  if (to.meta.requiresAdmin && !isAdmin) {
    ElMessage.error('没有权限访问该页面')
    next('/dashboard')
    return
  }

  // Redirect to dashboard if already logged in and trying to access public pages (except Home)
  if (to.meta.public && isLoggedIn && to.name !== 'Home' && to.path !== '/:pathMatch(.*)*') {
    next('/dashboard')
    return
  }

  next()
})

router.afterEach(() => {
  NProgress.done()
})

export default router
